import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  StringParam,
  withDefault,
  useQueryParams,
  NumberParam,
} from 'use-query-params';
import { debounce } from 'throttle-debounce';
import Input from '@welldev-group/rcl-input';
import Icon from '@welldev-group/rcl-icon';
import { I18nText, SortingPill } from '@welldev-group/react-comp-lib';
import routes from '../../../../../../shared/routes';
import httpClient from '../../../../../../shared/libraries/httpClient';
import PaginationWithTranslation from '../../../../../../shared/components/Pagination/PaginationWithTranslation';
import withQueryParamsProvider from '../../../../../../shared/components/withQueryParamsProvider';
import UserTable from './UserTable';
import UserModal from '../../../../User/UserModal';

const UserList = ({
  id,
  status,
  signedInAsSuperAdmin,
  permissionTemplateOptions,
  permissionOptions,
  urls,
  userAffiliateId,
  currentUserId,
}) => {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [query, setQuery] = useQueryParams({
    tab: withDefault(StringParam, 'users'),
    q: withDefault(StringParam, ''),
    state: withDefault(StringParam, status),
    page: NumberParam,
    sortColumn: StringParam,
    sortDirection: StringParam,
  });
  const PENDING_STATUS = 'pending';
  const isStatePending = status === PENDING_STATUS;

  const { tab, q, page, sortColumn, sortDirection } = query;

  const [localSearchQuery, setLocalSearchQuery] = useState(q);

  const setInitialParams = (data, searchQuery, state) => {
    // We only set the params onces when there is nothing in the url
    if (!query.page) {
      setQuery(
        {
          q: searchQuery,
          page: 1,
          sortColumn: data.sort.column,
          sortDirection: data.sort.direction,
          state,
        },
        // use replaceIn as we don't want another navigation to be added to the browser history
        'replaceIn',
      );
    }
  };

  const setSearchQueryInParams = React.useRef(
    debounce(500, value => setQuery({ q: value, page: 1 })),
  ).current;

  const setSearchQuery = value => {
    setIsLoading(true);
    setLocalSearchQuery(value);
    setSearchQueryInParams(value);
  };

  const fetchUsers = async (
    searchQuery,
    newState,
    newPage,
    column,
    direction,
  ) => {
    const params = {
      affiliate_id: id,
      state: status,
      q: searchQuery,
      page: newPage,
      column,
      direction,
    };
    const fetchRoute = isStatePending
      ? routes.users.listInvites(params)
      : routes.users.list(params);
    const { data } = await httpClient.get(fetchRoute);
    setUsers(data.users);
    setInitialParams(data, searchQuery, newState);
    setTotalPages(data.pagination.totalPages);
    setIsLoading(false);
  };

  const toggleDirection = () => {
    if (sortDirection === 'up') {
      setQuery({ sortDirection: 'down' });
    } else {
      setQuery({ sortDirection: 'up' });
    }
  };

  const setSortUser = chosenColumn => {
    if (sortColumn === chosenColumn) {
      toggleDirection();
    } else {
      setQuery({ sortColumn: chosenColumn, sortDirection: 'up' });
    }
  };

  const resendInvite = userId =>
    httpClient.put(routes.users.resendInvite({ userId })).then(response => {
      fetchUsers(q, status, page, sortColumn, sortDirection);
      return response;
    });

  const verifyEmail = userId =>
    httpClient.put(routes.users.verifyEmail({ userId })).then(response => {
      fetchUsers(q, status, page, sortColumn, sortDirection);
      return response;
    });

  const reverifyEmail = userId =>
    httpClient.put(routes.users.reverifyEmail({ userId })).then(response => {
      fetchUsers(q, status, page, sortColumn, sortDirection);
      return response;
    });

  const deleteUser = userId =>
    httpClient.delete(routes.users.destroy({ userId })).then(response => {
      fetchUsers(q, status, page, sortColumn, sortDirection);
      return response;
    });

  const reactivateUser = userId =>
    httpClient.put(routes.users.reactivate({ userId })).then(response => {
      fetchUsers(q, status, page, sortColumn, sortDirection);
      return response;
    });

  useEffect(() => {
    setQuery({ tab: 'users', state: status, page: 1 }, 'replaceIn');
  }, [status]);

  useEffect(() => {
    setQuery({ tab: 'users' }, 'replaceIn');
    fetchUsers(q, status, page, sortColumn, sortDirection);
  }, [q, tab, page, sortColumn, sortDirection]);

  return (
    <div>
      <div className="col-grid col-4 col-bleed affiliate-user-list__search-input">
        <Input
          placeholder={I18n.t(
            'components.affiliate_three_sixty.affiliate.users.search',
          )}
          preIcon={<Icon name="search" />}
          onChange={setSearchQuery}
          value={localSearchQuery}
        />
      </div>
      <div className="col-grid col-bleed direction-row justify-space-between">
        <span className="col-grid col-bleed-x direction-row">
          <span className="affiliate-user-list__sort">
            <SortingPill
              size="small"
              parameterName={I18n.t(
                'components.affiliate_three_sixty.affiliate.users.table.header.name',
              )}
              sortDirection={sortColumn === 'name' ? sortDirection : null}
              onClick={() => setSortUser('name')}
            />
          </span>
          <span className="affiliate-user-list__sort">
            <SortingPill
              size="small"
              parameterName={I18n.t(
                'components.affiliate_three_sixty.affiliate.users.table.header.email',
              )}
              sortDirection={sortColumn === 'email' ? sortDirection : null}
              onClick={() => setSortUser('email')}
            />
          </span>
          <span className="affiliate-user-list__sort">
            <SortingPill
              size="small"
              parameterName={
                isStatePending ? (
                  <I18nText id="affiliate_three_sixty.affiliate.users.table.header.invited_on" />
                ) : (
                  <I18nText id="affiliate_three_sixty.affiliate.users.table.header.last_active" />
                )
              }
              sortDirection={
                sortColumn === 'last_active' || sortColumn === 'invited_at'
                  ? sortDirection
                  : null
              }
              onClick={() =>
                setSortUser(isStatePending ? 'invited_at' : 'last_active')
              }
            />
          </span>
        </span>
        <span className="col-grid col-bleed-x direction-row justify-end">
          <a
            className="button button--v2 button--small user-index__create-actions-manage-team-button"
            href={urls.teams}
          >
            <I18nText id="team.manage" />
          </a>
          <UserModal
            userAffiliateId={userAffiliateId}
            permissionTemplateOptions={permissionTemplateOptions}
            permissionOptions={permissionOptions}
            fetchUsersByAffiliate={fetchUsers}
            linkText={<I18nText id="user.actions.send_invite" />}
            title={<I18nText id="user.invite.title" />}
            showPasswordFields={false}
            showAffiliateSelection={signedInAsSuperAdmin}
            submitURL={routes.users.invite}
            linkClassName="button button--v2 button--small"
          />
          {signedInAsSuperAdmin && (
            <UserModal
              permissionTemplateOptions={permissionTemplateOptions}
              permissionOptions={permissionOptions}
              fetchUsersByAffiliate={fetchUsers}
              linkText={<I18nText id="user.actions.create" />}
              title={<I18nText id="user.create.title" />}
              showPasswordFields={true}
              showAffiliateSelection={true}
              submitURL={routes.users.create}
              linkClassName="button button--v2 button--small button--primary"
            />
          )}
        </span>
      </div>
      <UserTable
        users={users}
        isLoading={isLoading}
        verifyEmail={verifyEmail}
        reverifyEmail={reverifyEmail}
        fetchUsers={fetchUsers}
        status={status}
        resendInvite={resendInvite}
        deleteUser={deleteUser}
        reactivateUser={reactivateUser}
        permissionOptions={permissionOptions}
        permissionTemplateOptions={permissionTemplateOptions}
        currentUserId={currentUserId}
        userAffiliateId={userAffiliateId}
        signedInAsSuperAdmin={signedInAsSuperAdmin}
      />
      {!isLoading && (
        <div className="affiliate-list__pagination">
          <PaginationWithTranslation
            currentPage={page}
            totalPages={totalPages}
            onPaginationClick={value => setQuery({ page: value })}
          />
        </div>
      )}
    </div>
  );
};

UserList.defaultProps = {
  permissionTemplateOptions: [],
};

UserList.propTypes = {
  id: PropTypes.number.isRequired,
  status: PropTypes.string.isRequired,
  signedInAsSuperAdmin: PropTypes.bool.isRequired,
  userAffiliateId: PropTypes.number.isRequired,
  urls: PropTypes.shape({
    users: PropTypes.string.isRequired,
    invites: PropTypes.string.isRequired,
    teams: PropTypes.string.isRequired,
  }).isRequired,
  permissionOptions: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string))
    .isRequired,
  permissionTemplateOptions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      permissions: PropTypes.arrayOf(PropTypes.string).isRequired,
    }),
  ),
  currentUserId: PropTypes.number.isRequired,
};

export default withQueryParamsProvider(UserList);
